/**
 * i18n facade — provides typed translation function for components.
 * In production, replace with next-intl's useTranslations; this is a fallback
 * for components that need to render outside provider context (e.g., Storybook).
 */
import { en, type EnMessages } from './en';
import { frCA, type FrCAMessages } from './fr-CA';

export type Locale = 'en' | 'fr-CA';
export const locales: Locale[] = ['en', 'fr-CA'];
export const defaultLocale: Locale = 'en';

/**
 * Structural dictionary type — both en and fr-CA share the same key shape
 * (verified by EnMessages and FrCAMessages having identical keys with locale-
 * specific literal values). Using a structural intersection lets TS see them
 * as the same shape despite the literal-type divergence.
 */
export type Dictionary = {
  readonly [K in keyof EnMessages]: EnMessages[K];
} & {
  readonly [K in keyof FrCAMessages]: FrCAMessages[K];
};

export const dictionaries: Record<Locale, Dictionary> = {
  en: en as unknown as Dictionary,
  'fr-CA': frCA as unknown as Dictionary,
};

export type Messages = EnMessages | FrCAMessages;

/**
 * Lightweight translation function that accepts a dotted key.
 * Falls back to en if locale missing or key not found.
 *
 * @example
 *   t('auth.signIn', 'en')             // => 'Sign in'
 *   t('auth.signIn', 'fr-CA')         // => 'Se connecter'
 *   t('errors.fileTooLarge', 'en', { limit: '5 MB' })  // => 'File too large — exceeds the 5 MB limit'
 */
export function t(
  key: string,
  locale: Locale = defaultLocale,
  vars?: Record<string, string | number>
): string {
  const dict = dictionaries[locale] ?? dictionaries[defaultLocale];
  const fallback = dictionaries[defaultLocale];

  const value = getByPath(dict, key) ?? getByPath(fallback, key) ?? key;

  if (typeof value !== 'string') return key;
  if (!vars) return value;

  return value.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined ? String(vars[k]) : `{${k}}`
  );
}

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, segment) => {
    if (acc == null) return undefined;
    return (acc as Record<string, unknown>)[segment];
  }, obj);
}

export { en, frCA };
export type { EnMessages, FrCAMessages };
